/*
  The stacked-mode sort control's state mapping (§6.3).

  Below md a row is a card, so there are no <th> elements and aria-sort has nowhere to
  live. The sort control moves outside the table: a select naming the column, a select
  naming the direction, and those two carry the accessible sort state that the header
  carried at wider widths.

  Selects hold strings, the table holds { orderBy, order }. That translation is the whole
  of this module, and it is here as plain functions because component tests do not run in
  this repo (RTL 13 against React 17, spec §2) — this is the unit the plan asks for.

  Two edges it has to get right:
    - orderBy can name a column that is not in this table, or one that cannot sort. The
      three tables all set an initial orderBy of their own (SetViewTable.tsx:54 and
      friends), and a column can be dropped — D24 drops GameSetsTable's Category. The
      control must still show something rather than an empty select, and a direction
      change must not write the unusable orderBy back.
    - a <select> can only ever emit one of its own option values, but the handler is
      still given a string. An unrecognised one is ignored, never coerced.
*/

import { SortDirection } from "lib";

import { isColumnSortable, sortableColumns, findColumn } from "./columns";
import { DataTableColumn, TableSortState } from "./types";

export interface StackedSortOption {
  value: string;
  label: string;
}

export interface StackedSortSelection<TRow> {
  /* The column select's value. "" only when nothing in the table can sort. */
  columnValue: string;
  /* The direction select's value. Always one of the two. */
  directionValue: SortDirection;
  /* The column those two describe, after the fallback below. */
  column: DataTableColumn<TRow> | null;
}

/* One option per sortable column, in the table's own column order. */
export const buildStackedSortColumnOptions = <TRow>(
  columns: readonly DataTableColumn<TRow>[]
): StackedSortOption[] =>
  sortableColumns(columns).map((column) => ({
    value: column.id,
    label: column.label,
  }));

/*
  Direction wording follows the column. "Ascending" on a count column tells a visitor
  nothing; "Lowest first" tells them what they will see.
*/
export const buildStackedSortDirectionOptions = <TRow>(
  column: DataTableColumn<TRow> | null
): StackedSortOption[] => {
  const numeric = !!column && column.numeric === true;
  return [
    { value: "asc", label: numeric ? "Lowest first" : "A to Z" },
    { value: "desc", label: numeric ? "Highest first" : "Z to A" },
  ];
};

/*
  State -> selects. When orderBy names no sortable column the first sortable one stands
  in, so the select is never blank and never shows a value it did not offer.
*/
export const resolveStackedSortSelection = <TRow>(
  columns: readonly DataTableColumn<TRow>[],
  sort: TableSortState
): StackedSortSelection<TRow> => {
  const current = findColumn(columns, sort.orderBy);
  const column =
    current && isColumnSortable(current) ? current : sortableColumns(columns)[0] ?? null;

  return {
    columnValue: column ? column.id : "",
    directionValue: sort.order === "desc" ? "desc" : "asc",
    column,
  };
};

/*
  Column select -> state. The direction the visitor already chose is kept: they asked to
  sort by a different column, not to reset how. Returns null when the value names nothing
  sortable, which the caller reads as "ignore this change".
*/
export const stackedSortColumnChange = <TRow>(
  columns: readonly DataTableColumn<TRow>[],
  sort: TableSortState,
  value: string
): TableSortState | null => {
  const column = findColumn(columns, value);
  if (!column || !isColumnSortable(column)) {
    return null;
  }
  return { orderBy: column.id, order: sort.order === "desc" ? "desc" : "asc" };
};

/*
  Direction select -> state. orderBy is taken from the resolved selection, not from the
  raw sort, so flipping the direction while orderBy names a dropped column adopts the
  column the visitor can actually see rather than writing the dead id back.
*/
export const stackedSortDirectionChange = <TRow>(
  columns: readonly DataTableColumn<TRow>[],
  sort: TableSortState,
  value: string
): TableSortState | null => {
  if (value !== "asc" && value !== "desc") {
    return null;
  }
  const { column } = resolveStackedSortSelection(columns, sort);
  if (!column) {
    return null;
  }
  return { orderBy: column.id, order: value };
};

/*
  The accessible sort state, announced next to the control because there is no aria-sort
  to carry it. Worded with the same direction labels the select offers, so what is read
  out matches what is on screen.
*/
export const describeSortState = <TRow>(
  columns: readonly DataTableColumn<TRow>[],
  sort: TableSortState
): string => {
  const { column, directionValue } = resolveStackedSortSelection(columns, sort);
  if (!column) {
    return "Not sorted";
  }
  const direction = buildStackedSortDirectionOptions(column).filter(
    (option) => option.value === directionValue
  )[0];
  return `Sorted by ${column.label}, ${direction.label}`;
};
